import { useCallback, useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Container, UncontrolledTooltip } from "reactstrap";
import { format } from "date-fns";

import SearchBox from "Pages/Run/Components/SearchBox/SearchBox";
import ReactTable from "components/ReactTable/ReactTable";
import useHelpers from "Hooks/useHelpers";
import useAgentStatus from "Hooks/useAgentStatus";
import { IColumnFilters } from "Pages/Run/Run";
import RunColumnFilter from "Pages/Run/Components/RunColumnFilter/RunColumnFilter";
import usePageTitle from "Hooks/usePageTitle";

interface IMessage {
    initiator: string;
    message_text: string;
    message_time: string;
    message_type: string;
    method_info: string;
    name: string;
    parent_id: string;
    start_time: string;
}

export default function RunSearch() {
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [columnFilters, setColumnFilters] = useState<IColumnFilters>(
        getDefaultColumnFilters()
    );
    const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined);
    const [triggerGoToPageOne, setTriggerGoToPageOne] =
        useState<boolean>(false);

    usePageTitle("Message Search");
    const { formatMessageText, debounce } = useHelpers();

    useAgentStatus();

    // Reset the table to page 1 when columnFilters change
    useEffect(() => {
        setTriggerGoToPageOne((prev) => !prev);
    }, [columnFilters]);

    const onSetTriggerGoToPageOne = useCallback(() => {
        setTriggerGoToPageOne((prev) => !prev);
    }, []);

    const debouncedSetColumnFilters = useRef<any>(
        debounce(
            (filter: Partial<IColumnFilters>) =>
                setColumnFilters((prev) => ({ ...prev, ...filter })),
            300
        )
    );

    function getDefaultColumnFilters() {
        return {
            message_type: "",
            message_text: "",
            initiator: "",
            name: "",
        };
    }

    /** Wrapped in useCallback to prevent unnecessary fetching in SearchBox useEffect */
    const onSetMessages = useCallback((messages: IMessage[]) => {
        setMessages(messages);
    }, []);

    const onSetIsLoading = useCallback((loading: boolean) => {
        setIsLoading(loading);
    }, []);

    const onSetColumnFilters = useCallback(
        (filter: Partial<IColumnFilters>) => {
            debouncedSetColumnFilters.current(filter);
        },
        []
    );

    /** Columns for ReactTable */
    const columns = useMemo(() => {
        const runColumnFilterProps = {
            onSetColumnFilters,
        };
        return [
            {
                Header: "Time",
                accessor: "message_time",
                disableFilters: true,
                width: 100,
                Cell: ({ row, value }: { row: any; value: string }) => {
                    return (
                        <>
                            <span id={`time${row.index}`}>
                                {format(new Date(value), "MMM d, HH:mm:ss ")}
                            </span>
                            <UncontrolledTooltip target={`time${row.index}`}>
                                {format(new Date(value), "MMM d, y, HH:mm")}
                            </UncontrolledTooltip>
                        </>
                    );
                },
            },
            {
                Header: "Type",
                accessor: "message_type",
                width: 100,
                Cell: ({ value }: { value: string }) => {
                    switch (value) {
                        case "Created":
                            return (
                                <div className="text-info">
                                    <b>{value}</b>
                                </div>
                            );
                        case "Updated":
                            return (
                                <div className="text-success">
                                    <b>{value}</b>
                                </div>
                            );
                        case "Deleted":
                            return (
                                <div className="text-dark">
                                    <b>{value}</b>
                                </div>
                            );
                        case "Errored":
                            return (
                                <div className="text-danger">
                                    <b>{value}</b>
                                </div>
                            );
                        case "Warning":
                            return (
                                <div className="text-warning">
                                    <b>{value}</b>
                                </div>
                            );
                        case "Info":
                            return <div>{value}</div>;
                    }
                },
                Filter: ({ column }: any) => (
                    <RunColumnFilter
                        column={column}
                        {...runColumnFilterProps}
                    />
                ),
            },
            {
                Header: "Description",
                accessor: "message_text",
                width: 500,
                Cell: ({ value }: { value: string }) =>
                    formatMessageText(value),
                Filter: ({ column }: any) => (
                    <RunColumnFilter
                        column={column}
                        {...runColumnFilterProps}
                    />
                ),
            },
            {
                Header: "Initiator",
                accessor: "initiator",
                width: 100,
                Filter: ({ column }: any) => (
                    <RunColumnFilter
                        column={column}
                        {...runColumnFilterProps}
                    />
                ),
            },
            {
                Header: "Integration",
                accessor: "name",
                Cell: ({ value, row }: { value: string; row: any }) => (
                    <Link
                        to={`/run_details/${row.original.parent_id}`}
                        target="_blank"
                    >
                        {value}
                    </Link>
                ),
                Filter: ({ column }: any) => (
                    <RunColumnFilter
                        column={column}
                        {...runColumnFilterProps}
                    />
                ),
            },
        ];
    }, [formatMessageText, onSetColumnFilters]);

    const searchBoxProps = {
        onSetIsLoading,
        messages,
        onSetMessages,
        columnFilters,
        onSetColumnFilters,
        onSetTriggerGoToPageOne,
        parent: "RunSearch",
    };

    return (
        <Container fluid>
            {/* Search Box */}
            <SearchBox<IMessage> {...searchBoxProps} />
            {/* Table */}
            <ReactTable
                columns={columns}
                data={messages}
                isLoading={isLoading}
                showPagination
                triggerGoToPageOne={triggerGoToPageOne}
            />
        </Container>
    );
}
